/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Edit, Mail, Phone, Building2, Shield, Loader2, Save, Check, Star, Send, Award, X, UserPlus, Search, Upload, FileText, Trash2, Eye, Download, Printer, FileCheck, ScrollText, ShieldCheck, Calendar, Hash, MapPin, Globe, Users, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { cn } from '../../lib/utils';
import { formatCodeNAF } from '../../utils/codeNaf';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'actif', label: 'Actif', dot: 'bg-emerald-500' },
  { value: 'en_attente', label: 'En attente', dot: 'bg-amber-500' },
  { value: 'inactif', label: 'Inactif', dot: 'bg-gray-500' },
  { value: 'suspendu', label: 'Suspendu', dot: 'bg-red-500' },
];

const RCDS_OPTIONS = [
  { value: 'actif', label: 'RCDS Actif', dot: 'bg-emerald-500' },
  { value: 'en_cours', label: 'En cours', dot: 'bg-blue-500' },
  { value: 'a_renouveler', label: 'A renouveler', dot: 'bg-amber-500' },
  { value: 'expire', label: 'Expire', dot: 'bg-red-500' },
  { value: 'non_couvert', label: 'Non couvert', dot: 'bg-gray-400' },
];

const FTPE_OPTIONS = [
  { value: 'adherent', label: 'Adherent', dot: 'bg-blue-500' },
  { value: 'actif', label: 'Actif', dot: 'bg-emerald-500' },
  { value: 'en_attente', label: 'En attente', dot: 'bg-cyan-500' },
  { value: 'a_renouveler', label: 'A renouveler', dot: 'bg-amber-500' },
  { value: 'expire', label: 'Expire', dot: 'bg-red-500' },
  { value: 'non_adherent', label: 'Non adherent', dot: 'bg-gray-400' },
];

const DOS_OPTIONS = [
  { value: 'souscrit', label: 'Souscrit', dot: 'bg-emerald-500' },
  { value: 'paye', label: 'Paye', dot: 'bg-teal-500' },
  { value: 'en_attente', label: 'En attente', dot: 'bg-amber-500' },
  { value: 'brouillon', label: 'Brouillon', dot: 'bg-slate-500' },
  { value: 'non_souscrit', label: 'Non souscrit', dot: 'bg-gray-400' },
];

const DispositifSelect = ({ label, value, onChange, options, testId }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-medium text-gray-600">{label}</Label>
    <Select value={value || ''} onValueChange={onChange}>
      <SelectTrigger className="h-9" data-testid={testId}>
        <SelectValue placeholder="Selectionner..." />
      </SelectTrigger>
      <SelectContent>
        {options.map(opt => (
          <SelectItem key={opt.value} value={opt.value}>
            <span className="flex items-center gap-2">
              <span className={cn("w-2 h-2 rounded-full", opt.dot)} />
              {opt.label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

export const UpdateDispositifsModal = ({ membre, open, onClose, onSave, token }) => {
  const [status, setStatus] = useState('');
  const [rcds, setRcds] = useState('');
  const [ftpe, setFtpe] = useState('');
  const [dos, setDos] = useState('');
  const [genererCertificat, setGenererCertificat] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open && membre) {
      setStatus(membre.status || membre.statut || 'en_attente');
      setRcds(membre.statut_rcds || 'non_couvert');
      setFtpe(membre.statut_ftpe || 'non_adherent');
      setDos(membre.statut_dos || 'non_souscrit');
      setGenererCertificat(false);
      setResult(null);
    }
  }, [open, membre]);

  // Auto-enable certificate when status changes to actif
  useEffect(() => {
    if (membre && status === 'actif' && membre.status !== 'actif') {
      setGenererCertificat(true);
    }
  }, [status, membre]);

  const handleSave = async () => {
    setSaving(true);
    setResult(null);
    try {
      const res = await axios.put(
        `${API}/api/admin/membres/${membre.id}/dispositifs`,
        { status, statut_rcds: rcds, statut_ftpe: ftpe, statut_dos: dos, generer_certificat: genererCertificat },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = res.data;
      setResult(data);
      if (data.certificat_genere) {
        toast.success('Statuts modifies et Certificat O\'SCOP genere');
      } else if (data.changes?.length > 0) {
        toast.success(`${data.changes.length} statut(s) modifie(s)`);
      } else {
        toast.info('Aucune modification');
      }
      if (data.membre && onSave) onSave(data.membre);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setSaving(false);
    }
  };

  if (!membre) return null;

  const hasChanges = status !== (membre.status || membre.statut || 'en_attente') ||
    rcds !== (membre.statut_rcds || 'non_couvert') ||
    ftpe !== (membre.statut_ftpe || 'non_adherent') ||
    dos !== (membre.statut_dos || 'non_souscrit') ||
    genererCertificat;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="update-dispositifs-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-indigo-600" />
            Modifier les statuts de {membre.prenom} {membre.nom}
          </DialogTitle>
          <DialogDescription>
            Modifier les dispositifs et generer le certificat d'adhesion O'SCOP
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Statut principal */}
          <DispositifSelect label="Statut principal" value={status} onChange={setStatus} options={STATUS_OPTIONS} testId="select-status-principal" />

          {/* Grille 3 colonnes pour les dispositifs */}
          <div className="grid grid-cols-3 gap-3">
            <DispositifSelect label="RCDS" value={rcds} onChange={setRcds} options={RCDS_OPTIONS} testId="select-rcds" />
            <DispositifSelect label="FTPE" value={ftpe} onChange={setFtpe} options={FTPE_OPTIONS} testId="select-ftpe" />
            <DispositifSelect label="DOS" value={dos} onChange={setDos} options={DOS_OPTIONS} testId="select-dos" />
          </div>

          {/* Option certificat */}
          <label className={cn(
            "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all",
            genererCertificat ? "bg-indigo-50 border-indigo-300 ring-1 ring-indigo-200" : "bg-gray-50 border-gray-200 hover:bg-gray-100"
          )} data-testid="checkbox-certificat">
            <input type="checkbox" checked={genererCertificat} onChange={(e) => setGenererCertificat(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
            <div>
              <p className="text-sm font-medium text-gray-800">Generer le Certificat d'Adhesion O'SCOP</p>
              <p className="text-xs text-gray-500">Le certificat sera automatiquement enregistre dans la GED du membre</p>
            </div>
          </label>

          {/* Resultat */}
          {result && (
            <div className={cn(
              "p-3 rounded-lg border text-sm",
              result.certificat_genere ? "bg-emerald-50 border-emerald-200" : "bg-blue-50 border-blue-200"
            )} data-testid="dispositifs-result">
              <div className="flex items-center gap-2 mb-1">
                <Check className="h-4 w-4 text-emerald-600" />
                <span className="font-medium">{result.message}</span>
              </div>
              {result.changes?.length > 0 && (
                <ul className="ml-6 space-y-0.5">
                  {result.changes.map((c, i) => <li key={i} className="text-xs text-gray-600">{c}</li>)}
                </ul>
              )}
              {result.certificat_genere && result.certificat_info && (
                <p className="mt-1 ml-6 text-xs text-emerald-700">Certificat : {result.certificat_info.filename}</p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Fermer</Button>
          <Button onClick={handleSave} disabled={saving || !hasChanges} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-dispositifs-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Appliquer les modifications
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ==================== MODIFICATION DATE ADHESION FTPE ====================

